import { CompanyTripIncomeReportDto, CompanyTripIncomeReportResponse } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Pagination, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { exportService } from '@/services/exportService';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';

const PAGE_TITLE = 'Trip Income Report';
interface ReportRowProps extends CompanyTripIncomeReportDto {
  isSummaryField?: boolean;
}
const PageSize = 20;
const TripIncomeReport = () => {
  const [searchForm] = Form.useForm();
  const [report, setReport] = React.useState<CompanyTripIncomeReportResponse>();
  const [loading, setIsLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string; pageIndex: number }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
    pageIndex: 1,
  });
  React.useEffect(() => {
    setIsLoading(true);
    apiSdk.CompanyTripIncomeReportService.getTripIncomeReports({
      fromDate: filter?.fromDate,
      toDate: filter?.toDate,
      pageSize: PageSize,
      pageIndex: filter.pageIndex,
    })
      .then(res => {
        setReport(res);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const data = React.useMemo(() => {
    if (!report?.data?.length) {
      return [];
    }
    let { totalCancelledAmount, totalOutStandingAmount, totalRevenueAmount } = report;
    let reportData: ReportRowProps[] = [];
    reportData = reportData.concat(report.data);
    reportData.push({
      isSummaryField: true,
      revenue: totalRevenueAmount,
      cancelledAmount: totalCancelledAmount,
      outStandingAmount: totalOutStandingAmount,
    });
    return reportData;
  }, [report]);

  const columns = React.useMemo(() => {
    let cols: ColumnsType<ReportRowProps> = [
      {
        title: t('Departure Date'),
        ellipsis: true,
        width: 180,
        fixed: 'left',
        dataIndex: 'plannedDepartureDate',
        render: v => <b>{dateTimeDisplayFormat.originDateTime(v)}</b>,
        onCell: r => ({ colSpan: r.isSummaryField ? 8 : 1 }),
      },
      {
        title: t('Trip Name'),
        dataIndex: 'tripName',
        width: 150,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },

      {
        title: t('Source'),
        dataIndex: 'sourceStation',
        width: 120,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },
      {
        title: t('Destination'),
        dataIndex: 'destinationStation',
        width: 120,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },

      {
        title: t('No. of Passenger'),
        dataIndex: 'noOfPassenger',
        width: 140,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },
      {
        title: t('Occupancy Rate'),
        dataIndex: 'occupancyRate',
        width: 120,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },
      {
        title: t('Total Seats'),
        dataIndex: 'totalSeats',
        width: 120,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },
      {
        title: t('No. of Ticket'),
        dataIndex: 'noOfTicket',
        width: 120,
        ellipsis: true,
        onCell: r => ({ colSpan: r.isSummaryField ? 0 : 1 }),
      },
      {
        title: t('Revenue'),
        ellipsis: true,
        dataIndex: 'revenue',
        render: (v, record) => {
          if (record.isSummaryField) {
            return <b>{`Total: ${formatCurrency(v)}`}</b>;
          }
          return formatCurrency(v);
        },
      },

      {
        ellipsis: true,
        title: t('Cancelled Amount'),
        dataIndex: 'cancelledAmount',
        render: (v, record) => {
          if (record.isSummaryField) {
            return <b>{`Total: ${formatCurrency(v)}`}</b>;
          }
          return formatCurrency(v);
        },
      },
      {
        ellipsis: true,
        title: t('OutStanding Amount'),
        dataIndex: 'outStandingAmount',

        render: (v, record) => {
          if (record.isSummaryField) {
            return <b>{`Total: ${formatCurrency(v)}`}</b>;
          }
          return formatCurrency(v);
        },
      },
    ];

    return cols;
  }, []);

  const onTableChange = nextPageIndex => {
    setFilter(x => ({ ...x, pageIndex: nextPageIndex || 1 } as any));
  };

  const onSearch = ({ fromDate, toDate }: any) => {
    setFilter({
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
      pageIndex: 1,
    });
  };

  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportTripIncomeReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportTripIncomeReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toPdf();
    });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>
        <Table
          className="transport-report-table"
          loading={loading}
          size="small"
          bordered
          columns={columns}
          dataSource={data}
          pagination={false}
          rowClassName={v => (v.isSummaryField ? 'summary-row' : '')}
        />
        <div style={{ textAlign: 'end', marginTop: 10, marginBottom: 10 }}>
          <Pagination
            hideOnSinglePage={true}
            showSizeChanger={false}
            onChange={onTableChange}
            current={filter?.pageIndex || 1}
            pageSize={PageSize}
            total={report?.total}
            showTotal={total => <div>{`Total: ${total}`}</div>}
            size="default"
          />
        </div>
      </div>
    </MainContent>
  );
};
export default TripIncomeReport;
